import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Icon, Row, Table } from 'grab-ui';
import cx from 'classnames';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import ScribeDuck from 'ducks/scribe-duck';
import TrackGADuck from 'ducks/track-ga-duck';
import {
  getAmountDisplay,
  getDeliveryID,
  getOrderItemWeight,
  getSenderAndRecipientInfo,
  isShopifyOrder,
} from 'helper/order';
import { paymentMethodForDisplayV2 } from 'helper/payment';
import { getUserCountryCode } from 'helper/user';
import { HELP_CENTER } from 'constants/app-constants';
import { pushGAPageViewEvent } from 'helper/ga-helper';
import {
  checkBookAgainable,
  getCreatedTimeToRender,
  getMSDRecipientName,
  getReceiptRecipients,
} from 'helper/history-order';
import {
  historyBookingFilterSelector,
  historyDeliveriesV2Selector,
} from 'selectors/new-history-selectors';
import { selectedCitySelector, userInfoSelector } from 'selectors/app-selector';
import { paymentMethodsSelector } from 'selectors/booking-selectors';
import NewHistoryDuck from 'ducks/new-history-duck';
import DivButton from 'components/v2/div-button/div-button';
import OrderStatus from 'components/v2/order-status/order-status';
import CustomerInfo from 'components/v2/customer-info/customer-info';
import Dropdown from 'components/v2/drop-down/drop-down';
import ReceiptDownload from 'components/v2/previous-bookings-section/order-table-complex/receipt-download/receipt-download';
import OrderPaymentDetails from 'components/v2/order-payment-details';
import threeDots from 'public/images/three-dots-v2.svg';
import { useFilteredOrder } from './common-hooks';
import commonTable from './common-table.less';
import styles from './history-booking-section.less';
import { getDisplayDeliveryType } from '../../../helper/history-order/history-order';

const ExpandIcon = ({ expanded }) => (
  <Icon type={expanded ? 'down' : 'right'} />
);

ExpandIcon.propTypes = {
  expanded: PropTypes.bool,
};

ExpandIcon.defaultProps = {
  expanded: false,
};

const ExpandedOrder = ({ order, onBookAgain, onReportIssue }) => {
  const { countryCode } = useSelector(selectedCitySelector);
  const paymentMethods = useSelector(paymentMethodsSelector);
  const paymentMethodMap = useMemo(
    () => Object.fromEntries(paymentMethods.map(pm => [pm.paymentTypeID, pm])),
    [paymentMethods],
  );
  const canBookAgain = useMemo(
    () => (order ? checkBookAgainable(order) : false),
    [order],
  );

  if (_isEmpty(order)) {
    return null;
  }

  const orderPaymentDetails = _get(order, 'paymentMethods', []);
  const { sender, recipients } = getSenderAndRecipientInfo(order);
  const promoCode = _get(order, 'service.promoCode');
  const referenceCode = _get(order, 'referenceCode');
  const isShopify = isShopifyOrder(_get(order, 'thirdPartySource'));
  const shopifyMetaDatas = _get(order, 'shopifyMetaData', []);
  const isMSD = _get(order, 'steps.length') > 2;
  const weight = useMemo(() => getOrderItemWeight(order), [order]);

  return (
    <Row type="flex" className={styles.expandedOrder}>
      <Col xs={8} className={styles.leftPanel}>
        <CustomerInfo info={sender} isSender isViewMode />
        {recipients.map((r, index, allItems) => (
          <CustomerInfo
            key={`${r.keywords}-${r.phone}`}
            info={r}
            isSender={false}
            isViewMode
            index={index}
            noOfRecipients={allItems.length}
            countryCode={countryCode}
            isShopifyOrder={isShopify}
            shopifyMetaDatas={shopifyMetaDatas.filter(
              shopifyMetaData => shopifyMetaData.step_idx === index + 1,
            )}
          />
        ))}
      </Col>
      <Col xs={16} className={styles.rightPanel}>
        <Row type="flex" gutter={{ md: 20 }}>
          <Col xs={12} md={6}>
            <h6>{I18n.t('v2.orderTable.column.payment')}</h6>
            {orderPaymentDetails.length > 1 ||
            !_get(order, 'paymentMethod.type') ? (
              <OrderPaymentDetails orderPaymentDetails={orderPaymentDetails} />
            ) : (
              <span>
                {paymentMethodForDisplayV2({
                  paymentMethod: _get(order, 'paymentMethod', {}),
                  paymentMethodMap,
                  countryCode,
                })}
              </span>
            )}
          </Col>
          {promoCode && (
            <Col xs={12} md={6}>
              <h6>{I18n.t('v2.orderTable.column.promo')}</h6>
              <span>{promoCode}</span>
            </Col>
          )}
          <Col xs={12} md={6}>
            <h6>{I18n.t('v2.orderTable.column.bookingID')}</h6>
            <span className={styles.lineBreaker}>
              {isMSD
                ? _get(order, 'bookingCode', '--')
                : _get(order, 'orderID', '--')}
            </span>
          </Col>
          {referenceCode && (
            <Col xs={12} md={6}>
              <h6>{I18n.t('v2.orderTable.column.referenceCode')}</h6>
              <span>{referenceCode}</span>
            </Col>
          )}
          <Col xs={12} md={6}>
            <h6>{I18n.t('v2.orderTable.column.orderType.label')}</h6>
            <span>
              {I18n.t(
                `v2.orderTable.column.orderType.source.${_get(
                  order,
                  'source',
                  0,
                )}`,
              )}
            </span>
          </Col>
        </Row>
        <Row type="flex" gutter={{ md: 20 }}>
          <Col xs={12} md={6}>
            <h6>{I18n.t('v2.orderTable.column.itemType')}</h6>
            <span>{_get(order, 'steps[1].itemInfo.categoryName', '--')}</span>
          </Col>
          <Col xs={12} md={6}>
            <h6>{I18n.t('v2.orderTable.column.weight')}</h6>
            <span>
              {weight
                ? `${weight} kg`
                : I18n.t('v2.orderTable.column.defaultWeight')}
            </span>
          </Col>
          <Col xs={24} md={12}>
            <h6>{I18n.t('v2.orderTable.column.instruction')}</h6>
            <span className={styles.instruction}>
              {_get(order, 'noteToDriver', '--')}
            </span>
          </Col>
        </Row>
        <Row type="flex" className={styles.actionRow}>
          <ReceiptDownload order={order} />
          <span className={styles.divider}>|</span>
          <DivButton
            onClick={onBookAgain(order)}
            className={cx(styles.divButton, {
              [styles.divButtonDisable]: !canBookAgain,
            })}
          >
            {I18n.t('v2.orderTable.action.bookAgain')}
          </DivButton>
          <span className={styles.divider}>|</span>
          <DivButton
            onClick={onReportIssue(order)}
            className={styles.divButton}
          >
            {I18n.t('v2.orderTable.action.reportIssue')}
          </DivButton>
        </Row>
      </Col>
    </Row>
  );
};

ExpandedOrder.propTypes = {
  order: PropTypes.shape({}),
  onBookAgain: PropTypes.func,
  onReportIssue: PropTypes.func,
};

ExpandedOrder.defaultProps = {
  order: undefined,
  onBookAgain: () => {},
  onReportIssue: () => {},
};

const OrderActionColumn = ({ order, onBookAgain, onReportIssue }) => {
  const dispatch = useDispatch();

  const trackAnalytics = () => {
    if (_isEmpty(order)) return;
    dispatch(
      TrackGADuck.creators.trackGAExpressBookingsWebClick(
        'PAST_BOOKINGS_DETAILS',
        { ORDER_ID: order.deliveryID },
      ),
    );
    dispatch(
      ScribeDuck.creators.scribeExpressBookingsWebHistoryTableAction(order),
    );
  };

  const stopProp = useCallback(e => {
    // prevent to expand column
    e.stopPropagation();
  }, []);

  const dropdownItems = useMemo(
    () => {
      if (_isEmpty(order)) return [];

      return [
        {
          value: <ReceiptDownload order={order} simpleDownloadBtn />,
          disabled: _isEmpty(getReceiptRecipients(order)),
        },
        {
          value: I18n.t('v2.orderTable.action.bookAgain'),
          disabled: !checkBookAgainable(order),
          onClick: onBookAgain(order),
        },
        {
          value: I18n.t('v2.orderTable.action.reportIssue'),
          onClick: onReportIssue(order),
        },
      ];
    },
    [order, onBookAgain, onReportIssue],
  );
  return (
    <div onClick={stopProp}>
      <Dropdown
        items={dropdownItems}
        placement={'bottomLeft'}
        className={commonTable.actionDropdown}
        childrenWrapClassName={commonTable.triggerWrapClassName}
        onVisible={trackAnalytics}
      >
        <img src={threeDots} alt="" />
      </Dropdown>
    </div>
  );
};

OrderActionColumn.propTypes = {
  order: PropTypes.shape({}),
  onBookAgain: PropTypes.func,
  onReportIssue: PropTypes.func,
};

OrderActionColumn.defaultProps = {
  order: undefined,
  onBookAgain: () => {},
  onReportIssue: () => {},
};

const HistoryBookingTable = ({ rebookBookings }) => {
  // data processing
  const dispatch = useDispatch();
  const userInfo = useSelector(userInfoSelector);
  const { deliveries, loading } = useSelector(historyDeliveriesV2Selector);
  const { orderType, orderState } = useSelector(historyBookingFilterSelector);

  // order filtering
  const ordersToDisplay = useFilteredOrder(deliveries, orderState, orderType);

  // table props
  const onBookAgain = useCallback(order => () => rebookBookings([order]), [
    rebookBookings,
  ]);

  // report issue
  const onReportIssue = useCallback(
    order => () => {
      const countryCode = getUserCountryCode(userInfo);
      const helpCenterLink = HELP_CENTER[countryCode] || HELP_CENTER.SG;
      pushGAPageViewEvent('HELP', userInfo);
      window.open(helpCenterLink);
    },
    [userInfo],
  );

  const onSelectOrder = useCallback(
    (_, selectedOrders) => {
      dispatch(
        NewHistoryDuck.creators.updateHistoryBookingSelection(selectedOrders),
      );
    },
    [dispatch],
  );
  useEffect(
    () => () => {
      onSelectOrder(undefined, []);
    },
    [onSelectOrder],
  );

  const columns = useMemo(
    () => [
      {
        title: I18n.t('v2.orderTable.column.date'),
        key: 'date',
        render: order => getCreatedTimeToRender(order),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.sender'),
        dataIndex: 'steps',
        key: 'sender',
        render: steps => _get(steps, '[0].contact.name'),
        width: '10%',
      },
      {
        title: I18n.t('v2.orderTable.column.recipient'),
        dataIndex: 'steps',
        key: 'recipient',
        render: (_, order) => getMSDRecipientName(order),
        width: '10%',
      },
      {
        title: I18n.t('v2.orderTable.column.deliveryType'),
        dataIndex: 'serviceDisplayInfo',
        key: 'serviceDisplayInfo',
        render: (_, order) => getDisplayDeliveryType(order),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.price'),
        dataIndex: 'amount',
        key: 'price',
        render: (amount, order) =>
          getAmountDisplay(amount, order.currency, false),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.orderState'),
        key: 'status',
        // eslint-disable-next-line
        render: order => <OrderStatus order={order} />,
        width: '25%',
      },
      {
        title: '',
        key: 'action',
        width: '5%',
        // eslint-disable-next-line
        render: order => (
          <OrderActionColumn
            order={order}
            onBookAgain={onBookAgain}
            onReportIssue={onReportIssue}
          />
        ),
      },
    ],
    [],
  );

  const renderExpandedOrder = useCallback(
    order => (
      <ExpandedOrder
        order={order}
        onBookAgain={onBookAgain}
        onReportIssue={onReportIssue}
      />
    ),
    [onBookAgain, onReportIssue],
  );

  return (
    <Table
      columns={columns}
      dataSource={ordersToDisplay}
      rowSelection={{ onChange: onSelectOrder }}
      className={cx(commonTable.table, styles.historyBookingTable)}
      pagination={{ pageSize: 10 }}
      expandedRowRender={renderExpandedOrder}
      expandIcon={ExpandIcon}
      expandRowByClick
      loading={loading}
      rowKey={getDeliveryID}
    />
  );
};

HistoryBookingTable.propTypes = {
  rebookBookings: PropTypes.func.isRequired,
};

export default HistoryBookingTable;
